import React, { useEffect } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Grid, Chip,
  Stack, Button, Divider, List, ListItem, ListItemIcon, ListItemText,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Check, Handyman, ExpandMore, Build, RequestQuote,
  Euro, Assignment, Verified, ArrowForward
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';

const LogicielDevisFactureArtisan = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Logiciel devis facture artisan bâtiment | Facteio';

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setProp = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Logiciel devis facture artisan bâtiment. TVA 5,5%/10%/20%, acomptes chantier, devis professionnel clairs. Conforme facture électronique 2026. Essai gratuit.');
    setMeta('keywords', 'logiciel devis facture artisan, logiciel facturation artisan bâtiment, devis artisan, facture artisan, TVA bâtiment, acompte chantier');
    setProp('og:title', 'Logiciel devis facture artisan bâtiment | Facteio');
    setProp('og:description', 'Facteio : logiciel devis facture spécialisé artisan et bâtiment. TVA bâtiment, acomptes, devis chantier. Conforme 2026.');

    // Structured data JSON-LD
    let sd = document.getElementById('sd-artisan');
    if (!sd) {
      sd = document.createElement('script');
      sd.id = 'sd-artisan';
      sd.type = 'application/ld+json';
      document.head.appendChild(sd);
    }
    sd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Facteio",
      "applicationCategory": "BusinessApplication",
      "description": "Logiciel devis facture artisan bâtiment. TVA 5,5%/10%/20%, acomptes chantier, conformité 2026.",
      "url": "https://facteio.com/logiciel-devis-facture-artisan",
      "offers": { "@type": "Offer", "priceCurrency": "EUR", "price": "0", "description": "Essai gratuit" }
    });

    return () => { const el = document.getElementById('sd-artisan'); if (el) el.remove(); };
  }, []);

  const goToRegisterTracked = () => {
    const params = new URLSearchParams({
      utm_source: 'site_facteio', utm_medium: 'cta',
      utm_campaign: 'logiciel_devis_facture_artisan', utm_content: 'cta_page'
    });
    navigate(`/register?${params.toString()}`);
  };

  return (
    <Box sx={(theme) => ({ background: theme.palette.background.default, minHeight: '100vh' })}>

      {/* Hero */}
      <Box sx={(theme) => ({
        background: `linear-gradient(135deg, #1F2937 0%, #374151 60%, #4B5563 100%)`,
        color: '#fff', py: { xs: 6, md: 10 }
      })}>
        <Container maxWidth="lg">
          <Button component={Link} to="/" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, fontWeight: 600, '&:hover': { color: '#fff' } }}>
            ← Retour à l'accueil
          </Button>

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mb={3}>
            <Chip icon={<Handyman sx={{ color: '#fff !important' }} />} label="Artisan & BTP" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700 }} />
            <Chip icon={<Check sx={{ color: '#fff !important' }} />} label="TVA bâtiment" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
            <Chip icon={<Check sx={{ color: '#fff !important' }} />} label="Acomptes chantier" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
            <Chip icon={<Check sx={{ color: '#fff !important' }} />} label="Conforme 2026" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
          </Stack>

          <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 900, lineHeight: 1.15, mb: 2 }}>
            Logiciel devis facture artisan bâtiment
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.1rem', md: '1.4rem' }, fontWeight: 400, opacity: 0.85, mb: 4, maxWidth: 680 }}>
            Électricien, plombier, menuisier, maçon : créez vos devis et factures en 2 minutes.
            TVA 5,5 %/10 %/20 %, acomptes, suivi chantier — conforme à la réforme facture électronique 2026.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={goToRegisterTracked}
              sx={{
                bgcolor: '#977F6B', '&:hover': { bgcolor: '#7A6358' },
                borderRadius: 50, px: 5, py: 1.8, fontSize: '1.05rem', fontWeight: 700
              }}
            >
              Essayer gratuitement
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/pricing')}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', color: '#fff', borderRadius: 50, px: 4, py: 1.8, fontWeight: 600 }}
            >
              Voir les tarifs
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>

        {/* Problème artisan */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} mb={1} textAlign="center">
            Le quotidien d'un artisan, c'est du terrain — pas de l'administratif
          </Typography>
          <Typography color="text.secondary" textAlign="center" mb={5} sx={{ maxWidth: 620, mx: 'auto' }}>
            Pourtant, un devis mal présenté peut faire rater un chantier. Une facture incomplète peut bloquer votre trésorerie.
          </Typography>

          <Grid container spacing={3}>
            {[
              { icon: <RequestQuote sx={{ color: '#977F6B' }} />, title: 'Devis chantier professionnel', text: 'Créez des devis détaillés avec ligne main d\'œuvre, ligne fournitures, et taux de TVA différenciés selon les postes.' },
              { icon: <Euro sx={{ color: '#977F6B' }} />, title: 'TVA bâtiment multi-taux', text: 'Gérez la TVA à 5,5 % (rénovation énergie), 10 % (rénovation logement) et 20 % (travaux neufs) sur le même document.' },
              { icon: <Assignment sx={{ color: '#977F6B' }} />, title: 'Acomptes sur chantier', text: 'Émettez une facture d\'acompte dès la signature du devis, puis soldez en fin de chantier. Tout est tracé automatiquement.' },
              { icon: <Verified sx={{ color: '#977F6B' }} />, title: 'Mentions légales artisan', text: 'Numéro SIRET, assurance décennale, garantie de parfait achèvement — les mentions obligatoires sont intégrées par défaut.' },
            ].map((item, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Card sx={{ height: '100%', border: '1px solid #E5E7EB', borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
                      {item.icon}
                      <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                    </Stack>
                    <Typography color="text.secondary">{item.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 8 }} />

        {/* Section TVA Bâtiment */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} mb={1}>
            TVA bâtiment : 5,5 % / 10 % / 20 %
          </Typography>
          <Typography color="text.secondary" mb={4} sx={{ maxWidth: 680 }}>
            La TVA dans le bâtiment, c'est complexe. Facteio la gère pour vous selon la nature des travaux,
            pour que chaque facture soit irréprochable.
          </Typography>

          <Grid container spacing={3}>
            {[
              { tva: '5,5 %', label: 'Rénovation énergétique', examples: ['Isolation thermique', 'Pompe à chaleur', 'Double vitrage', 'Chaudière à condensation'], color: '#D1FAE5', textColor: '#065F46' },
              { tva: '10 %', label: 'Rénovation logement ancien', examples: ['Plomberie (logement >2 ans)', 'Électricité (logement >2 ans)', 'Carrelage & revêtements', 'Maçonnerie renovation'], color: '#FEF3C7', textColor: '#92400E' },
              { tva: '20 %', label: 'Travaux neufs & autres', examples: ['Construction neuve', 'Travaux sur local commercial', 'Fournitures seules', 'Logement <2 ans'], color: '#FEE2E2', textColor: '#991B1B' },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card sx={{ height: '100%', border: '2px solid', borderColor: item.color, borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ bgcolor: item.color, borderRadius: 2, px: 2, py: 1, mb: 2, display: 'inline-block' }}>
                      <Typography fontWeight={900} fontSize="1.5rem" color={item.textColor}>TVA {item.tva}</Typography>
                    </Box>
                    <Typography fontWeight={700} mb={1.5}>{item.label}</Typography>
                    <List dense disablePadding>
                      {item.examples.map((ex, j) => (
                        <ListItem key={j} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}><Check sx={{ fontSize: 16, color: item.textColor }} /></ListItemIcon>
                          <ListItemText primary={ex} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 8 }} />

        {/* Comparatif */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} mb={4} textAlign="center">
            Avant / Après Facteio
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ border: '2px solid #FEE2E2', borderRadius: 3, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography fontWeight={800} color="error.main" mb={2} fontSize="1.1rem">❌ Sans Facteio</Typography>
                  {['Devis sur papier ou Word — pas professionnel', 'TVA calculée à la main — risque d\'erreur', 'Pas de suivi des acomptes versés', 'Oublis de mentions obligatoires (assurance décennale…)', 'Paiements en retard sans relance', 'Non prêt pour la facture électronique 2026'].map((t, i) => (
                    <Typography key={i} color="text.secondary" sx={{ mb: 1, pl: 1 }}>• {t}</Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ border: '2px solid #D1FAE5', borderRadius: 3, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography fontWeight={800} color="success.main" mb={2} fontSize="1.1rem">✅ Avec Facteio</Typography>
                  {['Devis pro en 2 minutes sur mobile ou bureau', 'TVA multi-taux automatique selon le chantier', 'Acomptes tracés et soldés automatiquement', 'Toutes les mentions légales intégrées', 'Relances automatiques pour les impayés', 'Conforme facture électronique 2026 dès maintenant'].map((t, i) => (
                    <Typography key={i} color="text.secondary" sx={{ mb: 1, pl: 1 }}>• {t}</Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 8 }} />

        {/* Réforme 2026 */}
        <Box mb={8}>
          <Card sx={{ background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)', color: '#fff', borderRadius: 3, p: { xs: 3, md: 5 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Chip label="Réforme obligatoire" sx={{ bgcolor: '#977F6B', color: '#fff', fontWeight: 700, mb: 2 }} />
                <Typography variant="h4" fontWeight={800} mb={2}>
                  Artisans : la facture électronique sera obligatoire en 2026
                </Typography>
                <Typography sx={{ opacity: 0.85, mb: 2 }}>
                  À partir du <strong>1er septembre 2026</strong>, toutes les entreprises françaises devront émettre
                  et recevoir des factures au format électronique. Facteio vous prépare dès aujourd'hui :
                  format conforme, transmission via portail public de facturation (PPF), sans surcoût.
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                  {['Format e-facture conforme', 'Transmission PPF intégrée', 'Aucune action technique requise'].map((t, i) => (
                    <Chip key={i} icon={<Check sx={{ color: '#fff !important' }} />} label={t} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/reforme-2026')}
                  sx={{ bgcolor: '#977F6B', '&:hover': { bgcolor: '#7A6358' }, borderRadius: 50, px: 4, py: 1.5, fontWeight: 700 }}
                >
                  En savoir plus
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Box>

        {/* FAQ */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} mb={4}>
            Questions fréquentes — Artisan
          </Typography>
          {[
            { q: 'Quel logiciel de devis pour un artisan du bâtiment ?', a: 'Facteio est conçu spécifiquement pour les artisans du bâtiment : il gère les devis multi-postes, la TVA à 5,5 %/10 %/20 %, les acomptes et les situations de travaux. Simple à prendre en main, même sans formation comptable.' },
            { q: 'Comment gérer la TVA bâtiment (5,5 %, 10 %, 20 %) sur une facture ?', a: 'Avec Facteio, vous sélectionnez le taux de TVA ligne par ligne sur chaque facture. Vous pouvez appliquer 5,5 % sur la partie isolation, 10 % sur la plomberie en rénovation, et 20 % sur la fourniture seule — tout sur le même document.' },
            { q: 'Comment facturer un acompte sur chantier ?', a: 'Facteio génère automatiquement une facture d\'acompte liée à votre devis. Lorsque le chantier est terminé, vous émettez la facture de solde qui déduit automatiquement les acomptes déjà versés.' },
            { q: 'Quelles mentions légales sont obligatoires sur une facture d\'artisan ?', a: 'Numéro SIRET, forme juridique, adresse, numéro de TVA intracommunautaire (si assujetti), mentions d\'assurance décennale et garantie de parfait achèvement. Facteio les intègre par défaut à chaque document.' },
            { q: 'La facture électronique est-elle obligatoire pour les artisans en 2026 ?', a: 'Oui. À partir du 1er septembre 2026, toutes les entreprises françaises (y compris les artisans) devront émettre leurs factures BtoB au format électronique. Facteio vous y prépare dès maintenant, sans surcoût.' },
          ].map((faq, i) => (
            <Accordion key={i} sx={{ mb: 1, border: '1px solid #E5E7EB', borderRadius: '12px !important', '&:before': { display: 'none' }, boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ '& .MuiAccordionSummary-content': { fontWeight: 700 } }}>
                {faq.q}
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Liens internes */}
        <Box mb={8}>
          <Typography variant="h5" fontWeight={700} mb={3}>Vous cherchez aussi :</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" gap={2}>
            <Button variant="outlined" component={Link} to="/logiciel-facture-auto-entrepreneur" sx={{ borderRadius: 50, fontWeight: 600 }}>
              Logiciel facture auto-entrepreneur →
            </Button>
            <Button variant="outlined" component={Link} to="/logiciel-facture-pme" sx={{ borderRadius: 50, fontWeight: 600 }}>
              Logiciel facture PME →
            </Button>
            <Button variant="outlined" component={Link} to="/reforme-2026" sx={{ borderRadius: 50, fontWeight: 600 }}>
              Réforme facture électronique 2026 →
            </Button>
          </Stack>
        </Box>

        {/* CTA final */}
        <Card sx={{ background: 'linear-gradient(135deg, #977F6B 0%, #7A6358 100%)', color: '#fff', borderRadius: 3, p: { xs: 3, md: 5 }, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={800} mb={1}>
            Prêt à facturer comme un pro ?
          </Typography>
          <Typography sx={{ opacity: 0.9, mb: 3, maxWidth: 520, mx: 'auto' }}>
            Moins de paperasse, plus de temps sur vos chantiers. Essayez Facteio gratuitement, sans carte bancaire.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={goToRegisterTracked}
            sx={{ bgcolor: '#fff', color: '#977F6B', borderRadius: 50, px: 5, py: 1.8, fontWeight: 700, '&:hover': { bgcolor: '#F3F4F6' } }}
          >
            Créer mon compte gratuit
          </Button>
        </Card>

      </Container>
    </Box>
  );
};

export default LogicielDevisFactureArtisan;
